import { QueryClient } from '@tanstack/react-query';
import type { QueryFunction } from '@tanstack/react-query';
import { showSuccessNotification, showErrorNotification } from '@/lib/notification';
import { normalizeCrmNumericPayload } from '@/lib/normalizeCrmNumericPayload';
import { debugContext } from '@/lib/debugContext';
import { ApiRequestError } from '@/lib/apiRequestError';
import { extractApiErrorDetails } from '@/lib/apiErrorDetails';
import { operationalUiErrorMessage } from '@/lib/operationalUiError';
import { removeCurrentCrmQueryCache } from '@/lib/persistedCrmQueryCache';
export { extractApiErrorDetails } from '@/lib/apiErrorDetails';

const HTTP_METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']);

/**
 * Rotte il cui body non deve mai finire nei log diagnostici (credenziali).
 * Il controllo diagnostico ripristinato logga i payload delle richieste
 * fallite nella Debug Console: le credenziali restano fuori per contratto.
 */
const SENSITIVE_BODY_ROUTE = /\/api\/(login|auth|email-accounts)|password|credential/i;

function summarizeRequestBody(url: string, body: unknown): unknown {
  if (typeof body !== 'string' || !body) return undefined;
  if (SENSITIVE_BODY_ROUTE.test(url)) return '[omesso: rotta con credenziali]';
  try {
    const parsed = JSON.parse(body);
    // Estrae i campi che discriminano la diagnostica di contesto (es. 409
    // EMAIL_DRAFT_CONTEXT_CHANGED) prima del troncamento del raw.
    const context = parsed && typeof parsed === 'object' && parsed.terminalContext && typeof parsed.terminalContext === 'object'
      ? {
          terminalType: (parsed.terminalContext as any).terminalType,
          entityType: (parsed.terminalContext as any).entityType,
          entityId: (parsed.terminalContext as any).entityId,
        }
      : undefined;
    return { ...(context ? { context } : {}), raw: body.slice(0, 4000) };
  } catch {
    return body.slice(0, 4000);
  }
}

type ApiRequestOptions = RequestInit & {
  /** A shorter deadline for interactive readers; default remains 30 seconds. */
  timeoutMs?: number;
  data?: unknown;
  /**
   * Alcune letture strutturate usano POST per trasportare filtri complessi
   * (per esempio `/api/crm-query/execute`). Non sono mutazioni: non devono
   * emettere l'evento globale di invalidazione, altrimenti la query attiva
   * invalida e rilancia se stessa in un ciclo.
   */
  isMutation?: boolean;
  /**
   * Le scritture esplicite del CRM mantengono la conferma generica. I flussi
   * interni (per esempio il trasporto di un turno Martina) possono invece
   * affidare il riscontro alla loro UI semantica, senza far apparire una falsa
   * "Creazione completata" dopo una semplice domanda.
   */
  notifySuccess?: boolean;
  /**
   * I pannelli che mostrano già lo stato dell'errore nel proprio corpo
   * disattivano il toast globale per evitare doppioni tecnici.
   */
  notifyError?: boolean;
};

/** Successful CRM writes are invalidation hints, never a parallel data store. */
export const CRM_MUTATION_COMMITTED_EVENT = 'crm:mutation-committed';

export type CrmRealtimeResource = 'emails' | 'contacts' | 'companies' | 'deals' | 'tasks' | 'meetings' | 'brands';

const resourceQueryPrefixes: Record<CrmRealtimeResource, readonly string[]> = {
  emails: ['/api/emails', '/api/email-accounts', '/api/email/'],
  contacts: ['/api/contacts', '/api/contact-emails', '/api/emails', '/api/email/messages', '/api/email/threads', '/api/email/entities'],
  companies: ['/api/companies'],
  deals: ['/api/deals', '/api/opportunities'],
  tasks: ['/api/tasks', '/api/activities'],
  meetings: ['/api/meetings', '/api/calendar', '/api/google-calendar'],
  brands: ['/api/brands'],
};

/** Keep event admission and query dependencies on the same resource registry. */
export function isCrmRealtimeResource(value: unknown): value is CrmRealtimeResource {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(resourceQueryPrefixes, value);
}

const crmQueryEntityByResource: Partial<Record<CrmRealtimeResource, string>> = {
  contacts: 'contact',
  companies: 'company',
  deals: 'deal',
  tasks: 'task',
  meetings: 'meeting',
  brands: 'brand',
};

export function isCrmEntityQueryKey(queryKey: readonly unknown[], entityType: string): boolean {
  if (queryKey[0] !== 'crm-query' || queryKey[1] !== 'execute') return false;
  const input = queryKey[2];
  return Boolean(input && typeof input === 'object' && (input as { entityType?: unknown }).entityType === entityType);
}

export function invalidateCrmEntityQueries(
  client: Pick<QueryClient, 'invalidateQueries'>,
  entityType: string,
) {
  return client.invalidateQueries({
    refetchType: 'active',
    predicate: (query) => isCrmEntityQueryKey(query.queryKey, entityType),
  });
}

/**
 * Invalidates only the read models affected by a CRM write.  The owner
 * WebSocket publishes the same resource type to other sessions; keeping this
 * mapping shared prevents an unrelated save from restarting every active
 * query (including Email, Martina and autocompletes).
 */
export async function invalidateCrmResourceQueries(
  client: Pick<QueryClient, 'cancelQueries' | 'invalidateQueries'>,
  resource: CrmRealtimeResource,
  committedAt = 0,
) {
  const prefixes = resourceQueryPrefixes[resource];
  const crmQueryEntity = crmQueryEntityByResource[resource];
  const predicate = (query: { queryKey: readonly unknown[]; state: { dataUpdatedAt: number }; observers: ReadonlyArray<{ options: { refetchInterval?: unknown } }> }) => {
      // Un onSuccess locale puo' avere gia' riallineato la query prima che
      // l'evento coalesciuto raggiunga AppLayout. Non trasformare una singola
      // mutation in due letture consecutive dello stesso endpoint.
      if (committedAt > 0 && query.state.dataUpdatedAt >= committedAt) return false;
      const key = query.queryKey[0];
      return shouldRefreshAfterCommittedCrmMutation(query)
        && ((typeof key === 'string' && prefixes.some((prefix) => key.startsWith(prefix)))
          || (resource === 'contacts' && typeof key === 'string' && /^\/api\/companies\/\d+\/contacts(?:[/?]|$)/.test(key))
          || Boolean(crmQueryEntity && isCrmEntityQueryKey(query.queryKey, crmQueryEntity)));
  };
  // A read started before the commit must not publish its old snapshot after
  // the writer. Cancel it first, then fetch the authoritative post-commit view.
  // Cancellation is control flow, not a failed read. Without silent, Query
  // publishes CancelledError and detail screens briefly become "not found"
  // immediately after a successful save. Keep the current data and replace
  // the obsolete request below, without reverting a newer writer snapshot.
  await client.cancelQueries({ predicate }, { revert: false, silent: true });
  return client.invalidateQueries({ refetchType: 'active', predicate });
}

export function crmResourceForMutationUrl(url: string): CrmRealtimeResource | null {
  const path = url.split('?')[0];
  if (path.startsWith('/api/contacts') || path.startsWith('/api/contact-emails')) return 'contacts';
  if (path.startsWith('/api/companies')) return 'companies';
  if (path.startsWith('/api/deals') || path.startsWith('/api/opportunities')) return 'deals';
  if (path.startsWith('/api/tasks') || path.startsWith('/api/activities')) return 'tasks';
  if (path.startsWith('/api/meetings') || path.startsWith('/api/calendar') || path.startsWith('/api/google-calendar')) return 'meetings';
  if (path.startsWith('/api/emails') || path.startsWith('/api/email/')) return 'emails';
  if (path.startsWith('/api/brands')) return 'brands';
  return null;
}

/**
 * The global write event is a coalesced hint for views that do not otherwise
 * refresh. A status query with its own polling cadence owns that cadence and
 * must only be refreshed by its direct mutation: restarting it after every
 * unrelated CRM write can cancel/restart an expensive operational read.
 */
export function shouldRefreshAfterCommittedCrmMutation(query: {
  observers: ReadonlyArray<{ options: { refetchInterval?: unknown } }>;
}): boolean {
  return !query.observers.some(({ options }) => {
    const refetchInterval = options.refetchInterval;
    return refetchInterval !== undefined && refetchInterval !== false;
  });
}

function announceCommittedCrmMutation(method: string, url: string) {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  window.dispatchEvent(new CustomEvent(CRM_MUTATION_COMMITTED_EVENT, {
    detail: { method, url, resource: crmResourceForMutationUrl(url), committedAt: Date.now() },
  }));
}

// Funzione per fare le chiamate API - retrocompatibile con i vecchi pattern di chiamata
export async function apiRequest(
  endpointOrMethod: string | Record<string, any>,
  urlOrOptions?: string | ApiRequestOptions,
  legacyBody?: any
): Promise<any> {
  let endpoint: string;
  let options: ApiRequestOptions = {};

  if (typeof endpointOrMethod === 'object' && endpointOrMethod !== null) {
    // Pattern: apiRequest({ url, method, data })
    const { url: u, method: m, data, ...rest } = endpointOrMethod;
    endpoint = u;
    options = { method: m || 'GET', ...rest };
    if (data !== undefined) options.body = JSON.stringify(data);
  } else if (typeof urlOrOptions === 'string') {
    // Pattern vecchio: apiRequest(method, url, body?)
    endpoint = urlOrOptions;
    options = { method: endpointOrMethod };
    if (legacyBody !== undefined) options.body = JSON.stringify(legacyBody);
  } else {
    // Pattern nuovo: apiRequest(endpoint, options?)
    endpoint = endpointOrMethod;
    options = urlOrOptions || {};
    if (options.data !== undefined) {
      options.body = JSON.stringify(options.data);
      delete options.data;
    }
  }

  // Assicuriamoci che l'endpoint inizi con /
  const url = typeof endpoint === 'string' && endpoint.startsWith('/') ? endpoint : `/${endpoint}`;

  const method = (options.method || 'GET').toUpperCase();
  if (['POST', 'PUT', 'PATCH'].includes(method) && typeof options.body === 'string') {
    try {
      options.body = JSON.stringify(normalizeCrmNumericPayload(JSON.parse(options.body)));
    } catch {
      // Body non JSON: viene lasciato invariato.
    }
  }
  const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;

  // Bug reale 2 ago 2026 (409 EMAIL_DRAFT_CONTEXT_CHANGED): `...options` era
  // sparso DOPO `headers`, quindi qualunque chiamante che passasse header
  // propri (es. `X-Martina-Email-Approval`) sostituiva l'intero oggetto e
  // perdeva `Content-Type: application/json` e `Authorization`. Senza
  // Content-Type `express.json()` non parsa il corpo: il server riceveva
  // `req.body` vuoto e leggeva un terminalContext inesistente. Gli header del
  // chiamante devono essere UNITI ai default, mai sostituirli.
  const { headers: callerHeaders, notifySuccess = true, notifyError = true, isMutation: mutationOverride, signal: callerSignal, timeoutMs, ...callerOptions } = options;
  const defaultOptions: RequestInit = {
    credentials: 'include',
    ...callerOptions,
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      ...(callerHeaders as Record<string, string> | undefined ?? {})
    },
  };

  // Il verbo HTTP da solo non identifica una scrittura: le query CRM usano
  // POST per un payload filtrabile. Il chiamante deve poter dichiarare la
  // semantica senza disattivare l'invalidazione delle vere mutazioni.
  const isWriteOperation = mutationOverride ?? ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  // Bug reale 19/08: fetch() senza AbortController puo' restare sospeso
  // per sempre se la connessione si interrompe senza una risposta HTTP (es.
  // il server viene riavviato a meta' richiesta durante un deploy). Ogni
  // flusso che disabilita i pulsanti "in attesa" (es. il debrief meeting di
  // MartinaMeetingBriefPanel) resta allora bloccato per sempre: il `finally`
  // che li riabilita non scatta mai perche' la promise non si risolve ne'
  // si rifiuta mai. Un timeout qui protegge OGNI chiamata dell'app, non solo
  // quella dove il blocco e' stato osservato.
  const REQUEST_TIMEOUT_MS = Number.isFinite(timeoutMs) && Number(timeoutMs) > 0
    ? Math.min(Number(timeoutMs), 30_000) : 30_000;
  const timeoutController = new AbortController();
  let timedOut = false;
  let abortedByCaller = false;
  const timeoutId = setTimeout(() => {
    timedOut = true;
    timeoutController.abort();
  }, REQUEST_TIMEOUT_MS);
  const abortForCaller = () => {
    // React Query annulla una lettura diventata obsoleta (ricerca successiva,
    // invalidazione dopo una scrittura, smontaggio). E' un esito previsto,
    // non un errore di rete e non deve sporcare Debug Console/Sentry.
    abortedByCaller = true;
    timeoutController.abort();
  };

  // React Query passa un AbortSignal quando una query diventa obsoleta (per
  // esempio mentre si continua a digitare in un autocomplete). In passato il
  // signal veniva sovrascritto da quello del timeout: la richiesta obsoleta
  // restava attiva fino a 30 secondi e poteva congestionare il picker e la
  // mailbox. Manteniamo un solo controller effettivo, ma propaghiamo entrambi
  // i motivi di annullamento; soltanto il nostro timer diventa TIMEOUT.
  if (callerSignal?.aborted) {
    abortForCaller();
  } else {
    callerSignal?.addEventListener('abort', abortForCaller, { once: true });
  }

  try {
    const response = await fetch(url, { ...defaultOptions, signal: timeoutController.signal });

    // Se la risposta non è ok, lanciamo un errore
    if (!response.ok) {
      const contentType = response.headers.get('content-type');

      // Check if we got HTML instead of JSON (common error)
      if (contentType && contentType.includes('text/html')) {
        console.error('🚨 Server returned HTML instead of JSON - possible server error');
        
        // In development, try to extract meaningful error from HTML
        if (process.env.NODE_ENV === 'development') {
          const htmlText = await response.text();
          if (htmlText.includes('Error:') || htmlText.includes('TypeError:')) {
            const errorMatch = htmlText.match(/Error:.*?(?=<|$)/i) || htmlText.match(/TypeError:.*?(?=<|$)/i);
            if (errorMatch) {
              throw new ApiRequestError(`Server Error: ${errorMatch[0]}`, { status: response.status });
            }
          }
        }
        
        throw new ApiRequestError(
          `Server error: Received HTML response instead of JSON (Status: ${response.status})`,
          { status: response.status },
        );
      }

      const errorText = await response.text();
      const errorDetails = extractApiErrorDetails(errorText);
      console.error('🚨 API Error Response:', { status: response.status, errorText });
      // Controllo diagnostico ripristinato (2 ago 2026): monitoredFetch non e'
      // piu' usato da apiRequest, quindi i payload delle richieste fallite non
      // arrivavano piu' alla Debug Console. Ora ogni errore API porta con se'
      // il body inviato (troncato, credenziali omesse), visibile con
      // Ctrl+Shift+D e in Sentry.
      debugContext.logError(`API Error: ${method} ${url} (${response.status})`, {
        status: response.status,
        errorText: errorText.slice(0, 2000),
        requestBody: summarizeRequestBody(url, options.body),
      }, { component: 'APIClient' });
      throw new ApiRequestError(errorDetails.message, {
        status: response.status,
        code: errorDetails.code,
        correlationId: errorDetails.correlationId,
        retryable: errorDetails.retryable,
        payload: contentType?.includes('application/json') ? (() => {
          try { return JSON.parse(errorText); } catch { return undefined; }
        })() : undefined,
      });
    }

    // Mostra notifica di successo per operazioni di scrittura
    if (isWriteOperation && notifySuccess) {
      // Determina il tipo di operazione
      let successMessage = 'Operazione completata';

      // Mappiamo il tipo di operazione a un messaggio specifico
      if (method === 'POST') {
        successMessage = 'Creazione completata';
      } else if (method === 'PUT') {
        successMessage = 'Aggiornamento completato';
      } else if (method === 'PATCH') {
        successMessage = 'Salvataggio completato';
      } else if (method === 'DELETE') {
        successMessage = 'Eliminazione completata';
      }

      showSuccessNotification(successMessage);
    }

    // Controlliamo se la risposta contiene dati JSON
    const contentType = response.headers.get('content-type');
    let responseData: unknown;
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      responseData = await response.text();
    }

    if (isWriteOperation) announceCommittedCrmMutation(method, url);
    return responseData;
  } catch (rawError) {
    const isAbortError = rawError instanceof DOMException && rawError.name === 'AbortError';
    if (abortedByCaller && isAbortError) {
      // Conserviamo l'AbortError perche' React Query lo riconosca come
      // cancellazione, ma non lo presentiamo come errore applicativo.
      throw rawError;
    }
    const error = timedOut && isAbortError
      ? new ApiRequestError('La richiesta non ha ricevuto risposta in tempo. Riprova.', { status: 0, code: 'TIMEOUT', retryable: true })
      : rawError;
    console.error('API request error:', error);

    // Show error notification for write operations
    if (isWriteOperation && notifyError) {
      showErrorNotification(operationalUiErrorMessage(error, 'L’operazione non è stata completata. I dati restano invariati.'));
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
    callerSignal?.removeEventListener('abort', abortForCaller);
  }
}

// Opzioni predefinite per le mutazioni
export const defaultMutationOptions = {
  onError: (error: Error) => {
    console.error('Mutation error:', error);
    // Già gestito nella funzione apiRequest
  }
};

/**
 * An unavailable auth-state lookup is a shared backend condition, not a
 * per-widget failure. Retrying every mounted query immediately recreates the
 * same request storm that made the mailbox unusable. The next explicit user
 * action or normal query invalidation can retry after the service recovers.
 */
export function shouldRetryCrmQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiRequestError) {
    if (error.code === 'AUTH_DATABASE_UNAVAILABLE' || error.code === 'TIMEOUT' || error.status === 401 || error.retryable === false) {
      return false;
    }
  }
  return failureCount < 1;
}

// Fetcher predefinito per React Query
const defaultQueryFn: QueryFunction = async (context) => {
  // Usiamo il primo elemento della queryKey come URL
  const url = context.queryKey[0] as string;
  return apiRequest(url, { method: 'GET' });
};

// Crea l'istanza di QueryClient
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: defaultQueryFn,
      retry: shouldRetryCrmQuery,
      refetchOnWindowFocus: false, // Disabilito il refresh automatico al focus
      staleTime: 5 * 60 * 1000, // 5 minuti prima di considerare i dati obsoleti
      // Deve essere almeno pari al maxAge della cache persistita: al refresh
      // la pagina rende subito l'ultimo read-model e rivalida in background
      // soltanto quando la query e' davvero stale o viene invalidata.
      gcTime: 60 * 60 * 1000,
      refetchInterval: false, // Disabilito il refresh automatico
    },
  },
});

// Funzione per svuotare completamente la cache e ricaricare i dati
export function clearCacheAndReloadData() {
  // Svuota completamente la cache
  removeCurrentCrmQueryCache();
  queryClient.clear();

  // Aggiungi un timestamp al localStorage per forzare il ricaricamento anche su altre pagine
  localStorage.setItem('cache_purge_timestamp', Date.now().toString());

  // Forza l'interruzione di tutte le query attive
  queryClient.cancelQueries();

  // Invalida tutte le query per forzare il recupero di dati freschi
  return queryClient.invalidateQueries();
}
